import type { Planbehandling, PlanbehandlingInput, PlanbehandlingDomains } from '@origo/planregister';
import { domainLabel } from '@origo/planregister';
import type { ProcessingFormValues, ProcessingItem, ProcessingYearGroup } from '../types';
import { formatDateShortNb } from './formatDate';

export const UNDATED_GROUP_KEY = 'undated';

// ---------- dato-hjelpere (ISO yyyy-mm-dd ↔ Date, UTC-midnatt) ----------

/** `Date` → `yyyy-mm-dd` for native date-input. Tom streng for ugyldig/manglende. */
export function toIsoDate(date: Date | undefined): string {
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` → `Date` (UTC-midnatt). `undefined` for tom/ugyldig streng. */
export function parseIsoDate(value: string): Date | undefined {
    if (!value) return undefined;
    const d = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? undefined : d;
}

// ---------- skjema ↔ domene ----------

export function emptyProcessingForm(defaults: { saksnummer?: string } = {}): ProcessingFormValues {
    return {
        navn: '',
        planbehandlingtype: '',
        dato: toIsoDate(new Date()),
        fradato: '',
        tildato: '',
        saksnummer: defaults.saksnummer ?? '',
        beskrivelse: '',
    };
}

export function toProcessingFormValues(b: Planbehandling): ProcessingFormValues {
    return {
        navn: b.navn,
        planbehandlingtype: b.planbehandlingtype,
        dato: toIsoDate(b.dato),
        fradato: toIsoDate(b.fradato),
        tildato: toIsoDate(b.tildato),
        saksnummer: b.saksnummer ?? '',
        beskrivelse: b.beskrivelse ?? '',
    };
}

/** Skjema → input for opprettelse. `planbehUid` genereres av kaller (immutabel). */
export function toPlanbehandlingInput(
    values: ProcessingFormValues,
    napId: string,
    planbehUid: string
): PlanbehandlingInput {
    return {
        planbehUid,
        nasjonalArealplanId: napId,
        navn: values.navn,
        planbehandlingtype: values.planbehandlingtype,
        dato: parseIsoDate(values.dato),
        fradato: parseIsoDate(values.fradato),
        tildato: parseIsoDate(values.tildato),
        saksnummer: values.saksnummer || undefined,
        beskrivelse: values.beskrivelse || undefined,
    };
}

/** Skjema → oppdatert behandling. Bevarer id-er og plan-tilknytning fra original. */
export function applyProcessingFormToPlanbehandling(
    original: Planbehandling,
    values: ProcessingFormValues
): Planbehandling {
    return {
        ...original,
        ...toPlanbehandlingInput(values, original.nasjonalArealplanId, original.planbehUid),
    };
}

// ---------- domene → visning ----------

export function toProcessingItem(b: Planbehandling, domains: PlanbehandlingDomains | undefined): ProcessingItem {
    const hasDate = Boolean(b.dato) && !Number.isNaN(b.dato!.getTime());
    return {
        id: b.planbehUid,
        objectId: b.objectId,
        tittel: b.navn,
        // Kode-agnostisk: visningstekst fra tjenestens domene, nøytral variant.
        typeBadge: { label: domainLabel(domains, 'planbehandlingtype', b.planbehandlingtype), variant: 'info' },
        dato: hasDate ? formatDateShortNb(b.dato) : undefined,
        saksnummer: b.saksnummer,
        missingDate: !hasDate,
        values: toProcessingFormValues(b),
    };
}

/**
 * Tidslinje iht. spec F5.3.1: kronologisk stigende på `dato`, gruppert per år
 * (eldste år først). Behandlinger uten dato samles i egen gruppe sist,
 * sortert på `objectId` som proxy for opprettelsesrekkefølge.
 */
export function toProcessingYearGroups(
    behandlinger: readonly Planbehandling[],
    domains: PlanbehandlingDomains | undefined
): ProcessingYearGroup[] {
    const dated = behandlinger
        .filter(b => b.dato && !Number.isNaN(b.dato.getTime()))
        .sort((a, b) => a.dato!.getTime() - b.dato!.getTime() || a.objectId - b.objectId);
    const undated = behandlinger
        .filter(b => !b.dato || Number.isNaN(b.dato.getTime()))
        .sort((a, b) => a.objectId - b.objectId);

    const byYear = new Map<number, ProcessingItem[]>();
    for (const b of dated) {
        const year = b.dato!.getUTCFullYear();
        const list = byYear.get(year) ?? [];
        list.push(toProcessingItem(b, domains));
        byYear.set(year, list);
    }

    const groups: ProcessingYearGroup[] = Array.from(byYear.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([year, items]) => ({ key: String(year), year, items }));

    if (undated.length > 0) {
        groups.push({
            key: UNDATED_GROUP_KEY,
            year: null,
            items: undated.map(b => toProcessingItem(b, domains)),
        });
    }

    return groups;
}
